import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query'
import { useIsHydratingRender } from './use-hydrating-render'

/**
 * A read for data the server cannot produce: a browser-only `queryFn`, a key built from `sessionStorage`,
 * or a shared list no route loader owns. Use it INSTEAD of `useQuery` there — it is the third sanctioned
 * shape of the `suspense-query/no-plain-use-query` rule.
 *
 * Suspense is not an option for these (the server would stall on a request that can't succeed, or block an
 * unrelated route's first paint), and a plain `useQuery` is a hydration hazard: whatever puts an entry in
 * the client cache first — the persisted localStorage blob, a streamed prefetch, another route — hands the
 * hydration render data the SSR HTML was built without, so React discards and re-creates the subtree (#418).
 *
 * So the value is withheld for the hydration render, which is the one that must match the server, and
 * served from the very next render (React schedules it). On a client-only mount — a dialog, a client-side
 * navigation — there is no hydration render and this is a plain read.
 */
export function useClientOnlyQuery<TQueryFnData, TError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): { data: TData | undefined; isPending: boolean } {
  const hydrating = useIsHydratingRender()
  const { data, isPending } = useQuery(options)
  return hydrating ? { data: undefined, isPending: true } : { data, isPending }
}
